/**
 * Catalyst Auto-Assignment Hook
 * 
 * For Catalyst/Enterprise tier shops, this hook handles:
 * 1. Calling smart-assign to get top-ranked technician
 * 2. Creating ticket with the top pick automatically
 * 3. Showing a toast with assignment reasoning
 */

import { resolvePlanningBufferPct } from '@/utils/planningBuffer';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { createVehicle } from '@/lib/api/vehicleApi';
import { decideTicketVehicleAction } from '@/components/tickets/ticketVehicleResolution';
import { toast } from '@/hooks/use-toast';
import { format, addMinutes } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useDate } from '@/contexts/DateContext';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { breadcrumbs } from '@/telemetry/breadcrumbs';
import { autoSmartAssign, isCatalystTier, buildAssignmentReason, type AutoAssignTicketData } from '@/utils/autoSmartAssign';
import { convertDatetimeLocalToUTC } from '@/utils/timezoneMath';
import { getShopDayLabel } from '@/utils/shopTime';
import { buildRolloverChain, resolveRolloverPartsForStart } from '@/utils/rolloverPlanClient';
import { logTicketCreated, logTicketAssigned } from '@/utils/ticketEventLogger';
import { logTicketEventToOcis } from '@/utils/ocisIngest';
import { calculateWallMinutes, calculateTicketOptiFinish } from '@/utils/canonicalTimeMath';
import { clock } from '@/utils/clock';
import { getTodaySafeStartTime } from '@/utils/getTodaySafeStartTime';
// Static-imported (was previously `await import(...)`) so these helpers
// ride the main bundle. A deploy that re-hashes the lazy chunk would
// otherwise 404 for browsers still holding the old `index.js`, surfacing
// as "Failed to fetch dynamically imported module" on Create Ticket.
import { pickCompatibleBayForTech } from '@/utils/pickCompatibleBayForTech';
import { buildBaySnapshotsForJobs } from '@/utils/catalogBayRules';
import { createTicketBundle } from '@/lib/tickets/createTicketBundle';
import { callProposalRefusalMessage } from '@/lib/callProposal/callProposalTicket';
import { notifyDayboardInvalidated } from '@/utils/dayboardInvalidation';
import { recordAssignmentSelection } from '@/lib/assignment/rankingAuditRecord';
import { readServerCapabilityActive } from '@/lib/entitlements/serverCapability';

interface UseCatalystAutoAssignOptions {
  onSuccess?: (ticketId: string) => void;
  onFallbackToModal?: () => void;
  /** Core-100 R5 (CORE-AQ): the server refused the ticket for its call proposal — nothing was created. */
  onCallProposalRefused?: (message: string) => void;
}

export function useCatalystAutoAssign(options: UseCatalystAutoAssignOptions = {}) {
  const navigate = useNavigate();
  const { activeShop } = useAuth();
  const { selectedDate, selectedDayLabel } = useDate();
  const { shopTimezone } = useShopTimezone();
  const [isAutoAssigning, setIsAutoAssigning] = useState(false);

  const isCatalystShop = isCatalystTier(activeShop?.tier);

  /**
   * Perform Catalyst auto-assignment for a ticket
   * Returns true if handled, false if should fall back to modal
   */
  const performAutoAssign = async (ticketData: AutoAssignTicketData): Promise<boolean> => {
    if (!activeShop?.id) {
      toast({
        title: 'Error',
        description: 'No active shop selected',
        variant: 'destructive',
      });
      return false;
    }

    setIsAutoAssigning(true);
    breadcrumbs.log('OB:COMMIT', { action: 'automatic_assign_start' });

    // SEC-H1 / FD-S3: automatic (Catalyst) dispatch is a server capability, not a browser decision. Entitlement alone
    // does not authorize it, and until earned-confidence autonomy (CAT-02) exists the platform keeps it off, so the
    // database refuses a browser-created automatic assignment. When the server says it is not active, go straight to
    // the ranked options (human-governed assignment) instead of attempting a commit that would be refused.
    const autoDispatchActive = await readServerCapabilityActive(activeShop.id, 'catalyst_auto_dispatch');
    if (autoDispatchActive === false) {
      breadcrumbs.log('OB:COMMIT', { action: 'automatic_assign_unavailable' });
      toast({
        title: 'Choose a technician',
        description: 'Automatic assignment is not available yet. Pick from the ranked options.',
      });
      setIsAutoAssigning(false);
      options.onFallbackToModal?.();
      return false;
    }

    try {
      // Call smart-assign and get top pick
      const result = await autoSmartAssign(
        { ...ticketData, shop_id: activeShop.id },
        selectedDate
      );

      // Fall back to modal for split-eligible tickets
      if (result.splitEligible) {
        toast({
          title: 'Review Required',
          description: 'This ticket is eligible for splitting. Please review options.',
        });
        setIsAutoAssigning(false);
        options.onFallbackToModal?.();
        return false;
      }

      // Fall back to modal if no availability
      if (!result.success || !result.topPick) {
        toast({
          title: 'No Availability',
          description: result.reason || 'No technician availability found. Searching for open slots...',
        });
        setIsAutoAssigning(false);
        options.onFallbackToModal?.();
        return false;
      }

      // We have a top pick - create the ticket automatically
      const topPick = result.topPick;
      const reason = result.reason;

      // Fetch technician data
      const { data: techData, error: techError } = await supabase
        .from('technicians')
        .select('efficiency_percentage, buffer_percentage, shift_start')
        .eq('id', topPick.technicianId)
        .single();

      if (techError || !techData) {
        throw new Error('Could not load technician data');
      }

      const techEff = techData.efficiency_percentage || 100;
      const techBuffer = resolvePlanningBufferPct(techData.buffer_percentage);

      // Vehicle: a selected existing vehicle ALWAYS wins (K19-2, as OptiPlace does);
      // otherwise the canonical writer creates one (reusing an exact-VIN match).
      let vehicleData;
      const vehicleAction = decideTicketVehicleAction({ vehicleId: ticketData.vehicleId, vehicle: ticketData.vehicle });
      if (vehicleAction.action === 'reuse') {
        vehicleData = { id: vehicleAction.vehicleId };
      } else if (ticketData.vehicle && ticketData.vehicle.year) {
        const newVehicleId = await createVehicle({
          shop_id: activeShop.id,
          year: ticketData.vehicle.year,
          make: ticketData.vehicle.make,
          model: ticketData.vehicle.model,
          engine_size: ticketData.vehicle.engineSize || null,
          vin: ticketData.vehicle.vin || null,
          license_plate: ticketData.vehicle.licensePlate || null,
        });
        vehicleData = { id: newVehicleId };
      }

      // Generate ticket ID using UUID for collision safety
      const ticketId = crypto.randomUUID();

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      const currentUserId = user?.id || null;

      // Determine start time - use selectedDayLabel to avoid timezone drift
      // R11-M-2: Convert waiter start time to proper UTC ISO string
      const waiterFixed = String(ticketData.type || '').toLowerCase() === 'waiter' && ticketData.startTime
        ? convertDatetimeLocalToUTC(`${selectedDayLabel}T${ticketData.startTime}`, shopTimezone)
        : null;

      // Chronomancer R-PAST-1: safe fallback for smart-assign returning
      // nothing. Previously fell through to `selectedDate.toISOString()`,
      // which is the browser's midnight on selectedDate. For a shop east
      // of the browser (e.g., NY shop viewed from LA), browser midnight
      // on the shop-local day is BEFORE that shop day began, which reads
      // as shop-local "yesterday" — inserting a ticket a full day in the
      // past with no guard. Route through the canonical safe-start helper
      // that already clamps drag-drop to max(shift_start_utc, now + 1min)
      // when selectedDay is today. For future days it returns shift start
      // UTC. `shift_start` fallback uses 8:00 if unknown (matching
      // CreateTicket's 08:00 default appointmentDateTime).
      const defaultShiftStart = '08:00';
      const safeFallback = getTodaySafeStartTime(
        defaultShiftStart,
        selectedDayLabel,
        shopTimezone,
      );
      // A rollover plan's Part A start comes first (a ranked rollover
      // candidate reports `suggestedStartTime: null`).
      const rankedRolloverStart = (topPick.rolloverParts?.length ?? 0) > 1
        ? topPick.rolloverParts![0].startTime
        : null;
      let startTimeToUse = rankedRolloverStart || topPick.suggestedStartTime || waiterFixed || safeFallback;

      // Chronomancer R-PAST-2: clock-aware past-time clamp with loud
      // feedback. Previously used browser `new Date()` (test-mode clock
      // freeze bypassed) and silently shifted the ticket to now+5min
      // without informing the advisor. Now reads through `clock.now()`
      // and emits a toast when the clamp kicks in so the advisor knows
      // the placement moved and can inspect / reassign if needed. This
      // is defense-in-depth: smart-assign's EFS should already return
      // future slots for today, but if the slot became stale between
      // suggestion and confirmation (network latency, user deliberation),
      // this clamp prevents a past-time insert.
      const startTimeMs = new Date(startTimeToUse).getTime();
      const nowMs = clock.nowMs();
      if (Number.isFinite(startTimeMs) && startTimeMs < nowMs) {
        const adjusted = new Date(nowMs + 5 * 60 * 1000).toISOString();
        console.warn(
          `[CatalystAutoAssign] Past-time clamp: shifted start_time from ${startTimeToUse} to ${adjusted}`,
        );
        toast({
          title: 'Start time adjusted to now',
          description:
            'The suggested slot had already elapsed. The ticket was placed 5 minutes from now.',
        });
        startTimeToUse = adjusted;
      }

      // Handle rollover tickets: the server's multi-day plan, re-planned from
      // the actual start when the past-time clamp moved it
      // (rolloverPlanClient.ts — the same planner smart-assign runs).
      let rolloverParts = topPick.rolloverParts || [];
      if (rolloverParts.length > 1) {
        const resolved = resolveRolloverPartsForStart({
          parts: rolloverParts,
          plan: topPick.rolloverPlan ?? null,
          startIso: startTimeToUse,
          shopTimezone,
        });
        if (!resolved.ok) {
          throw new Error('The multi-day plan no longer fits from the new start time. Refresh and try again.');
        }
        rolloverParts = resolved.parts;
      }
      // W4 (field test 2026-10-16, DECISION_LEDGER §1 / D-3): an AUTOMATIC assignment is never a rollover approval.
      // Catalyst creates the ticket WHOLE at the plan's first start; if it cannot finish today, the server lane walk
      // flags it and raises the end-of-day decision for an authorized person (Catalyst may recommend, never approve).
      // The continuation guard (trg_zz_rollover_continuation_requires_approval) refuses an automatic chain anyway.
      const planWouldRoll = rolloverParts.length > 1;
      if (planWouldRoll) startTimeToUse = rolloverParts[0].startTime;
      const willCreateRollover = false as boolean;

      // The persisted chain, shaped exactly as OptiPlace shapes it
      // (rolloverPlanClient.buildRolloverChain).
      const rolloverChain = willCreateRollover
        ? buildRolloverChain({
            rootTicketId: ticketId,
            parts: rolloverParts,
            totalBookMinutes: ticketData.predictedMinutes,
            efficiencyPct: techEff,
            bufferPct: techBuffer,
            shopTimezone,
            nowMs: clock.nowMs(),
          })
        : null;

      const mainPredictedMinutes = rolloverChain
        ? rolloverChain.root.predicted_minutes
        : ticketData.predictedMinutes;

      // Part A's shop-local date is its own planned workday.
      const mainScheduledDate = rolloverChain
        ? rolloverChain.root.scheduled_date
        : selectedDayLabel;

      // Honor parked-on-arrival selection — operators on Catalyst shops can
      // also pick a parking spot at create time. Mirrors the OptiPlace
      // shape so tickets_parking_lot_immunity doesn't fire (parking_lot=
      // true requires assigned_tech_id IS NULL); scheduled_tech_id carries
      // the dispatch plan so the Dayboard still surfaces the ticket under
      // the auto-assigned tech.
      const isParkedOnArrival = !!ticketData.parkingSpotId;

      const assignedBayId = isParkedOnArrival
        ? null
        : await pickCompatibleBayForTech({
            techId: topPick.technicianId,
            shopId: activeShop.id,
            // jobs omitted: ticket_jobs are written after the ticket INSERT below.
          });
      const assignedBay = assignedBayId ? { id: assignedBayId } : null;

      // ── CORE-AB CONVERGENCE (D1 · D2 · D3) ───────────────────────────
      //
      // This path used to persist from the browser in FOUR independent
      // statements — primary ticket, each rollover part, jobs, ticket_jobs —
      // and never reached `create_ticket_bundle` or
      // `__validate_ticket_placement`. That made CORE-AB a capability with TWO
      // assignment commit authorities, only one of which revalidated and only
      // one of which was atomic (CORE-AB-GAP-010, HARD-009).
      //
      // Everything ABOVE this point is unchanged: the same deterministic
      // `smart-assign`, the same `topPick`, the same rollover part maths,
      // parking decision, bay pick and timing clamp. Catalyst still owns the
      // fact that this shop opted into automatic selection. What changed is
      // only WHERE the required rows are written.
      //
      // D1 — rollover contract. A continuation is remaining time on the SAME
      // work, not a copy of it. The primary group owns the jobs and the
      // links; continuation groups carry `jobs: []` / `ticketJobs: []`, which
      // the RPC permits only because they carry `rollover_of_ticket_id`. The
      // duplicated per-rollover `ticket_jobs` this path used to write are
      // GONE (CORE-AB-GAP-011). The primitive was not taught to accept a
      // caller-supplied `job_id`.
      //
      // D2 — atomicity. Required creation state is all-or-nothing. A rollover
      // continuation that cannot be written now rolls back the primary, its
      // jobs and its links with it. FIX-R132-MED-7's non-fatal rollover
      // tradeoff is deliberately REVERSED for required rows: a primary
      // without its continuations is a partial ticket, and the operator
      // cannot see that from the Dayboard. Optional post-commit side effects
      // — OCIS events, cascades, notifications, the audit write — stay
      // non-fatal below, because telemetry must never destroy a valid ticket.
      //
      // D3 — provenance. `dispatch_assignment_source: 'automatic'` travels
      // INSIDE the same transaction as the dispatch plan it describes, so a
      // machine decision can never be read back as a human one.

      // Bay-rule snapshots are resolved BEFORE the commit now, because the
      // links are part of the same transaction rather than a later statement.
      const catalystBaySnapshots = await buildBaySnapshotsForJobs(
        activeShop.id,
        ticketData.jobs.map(j => ({
          catalog_job_id: (j as any).catalog_job_id ?? null,
          category: j.category ?? null,
        })),
      );

      // Map bay requirement
      const mapBayRequirement = (bay: string): string => {
        const bayLower = bay.toLowerCase();
        if (bayLower.includes('lift') || bayLower.includes('2-post') || bayLower.includes('4-post')) return 'Lift';
        if (bayLower.includes('flat')) return 'Flat';
        if (bayLower.includes('alignment')) return 'Alignment';
        if (bayLower.includes('specialty') || bayLower.includes('diagnostic')) return 'Specialty';
        return 'Lift';
      };

      const catalystJobsToInsert = ticketData.jobs.map((job) => ({
        category: job.category,
        submenu: job.submenu,
        name: job.name,
        standard_minutes: job.minutes,
        bay_requirement: mapBayRequirement(job.bay),
        skill_level: job.skill,
        proficiency_category: job.proficiency_category || null,
        catalog_job_id: (job as any).catalog_job_id ?? null,
        shop_id: activeShop.id,
      }));

      // `ticket_id` / `job_id` are deliberately ABSENT — the RPC injects both
      // from THIS group's inserted rows, so a link can never reference an id
      // that failed to commit.
      const catalystTicketJobsToInsert = ticketData.jobs.map((job, idx) => ({
        minutes_override: job.minutes,
        notes: job.notes || null,
        ...catalystBaySnapshots[idx],
      }));

      const primaryTicketPayload = {
        id: ticketId,
        // Core-100 R4 CORE-Y: the origin is recorded on every Create Ticket path, automatic placement included.
        creation_method: ticketData.creationMethod ?? 'create_ticket',
        // Core-100 R5 (CORE-AQ): created from a call proposal — confirmed by the database with this ticket.
        ...(ticketData.sourceCallProposalId ? { source_call_proposal_id: ticketData.sourceCallProposalId } : {}),
        simple_ticket_number: 0,
        vehicle_id: vehicleData?.id || null,
        customer_name: ticketData.customerName || null,
        customer_phone: ticketData.customerPhone || null,
        type: ticketData.type,
        priority: ticketData.priority,
        is_comeback: ticketData.isComeback,
        // Codex: vehicle_present derived from physical state — not written directly
        assigned_tech_id: isParkedOnArrival ? null : topPick.technicianId,
        // CORE-AB-GAP-008 / D3 — qualifies scheduled_tech_id, NOT custody.
        // Unconditional on purpose: a parked-on-arrival ticket has
        // assigned_tech_id NULL and scheduled_tech_id = topPick, and that is
        // still a Catalyst dispatch decision. Keying this to custody would
        // discard the provenance of the very ticket Catalyst just placed.
        dispatch_assignment_source: 'automatic',
        scheduled_tech_id: topPick.technicianId,
        original_tech_id: topPick.technicianId,
        // ── B5a: A DISPATCH PLAN IS NOT A PHYSICAL FACT ────────────────
        // `bay_id` means the car is physically inside that bay. An
        // auto-assigned booking proves nothing about where the car is, and
        // this path never had the occupancy guard, so the picker's candidate
        // went straight into the row. The plan survives in assigned_tech_id +
        // scheduled_tech_id + dispatch_assignment_source + start_time; the
        // physical bay is earned later through `move_vehicle_location`.
        //
        // PROMOTION MERGE 2026-09-14 — main's convergence rewrite reinstated
        // a `bay_id` write fed by `assignedBay` while rebuilding this path as
        // an atomic bundle. The atomicity is kept; the fabricated write is not.
        // `assignedBay` is still COMPUTED — B5a removes the persistence, not
        // the planning.
        //
        // parking_spot_id / parking_lot are UNCHANGED — a spot the operator
        // actually chose at arrival IS physical truth.
        bay_id: null,
        parking_spot_id: ticketData.parkingSpotId || null,
        parking_lot: isParkedOnArrival,
        start_time: startTimeToUse,
        scheduled_start_time: ticketData.type === 'Waiter' && ticketData.startTime
          ? convertDatetimeLocalToUTC(`${selectedDayLabel}T${ticketData.startTime}`, shopTimezone)
          : null,
        predicted_minutes: mainPredictedMinutes,
        due_out: ticketData.dueOut || null,
        status: 'Active',
        shop_id: activeShop.id,
        parent_ticket_id: willCreateRollover ? ticketId : null,
        sequence_index: willCreateRollover ? 1 : null,
        remaining_minutes_at_split: rolloverChain ? rolloverChain.root.remaining_minutes_at_split : null,
        split_reason: willCreateRollover ? 'EOD_ROLLOVER' : null,
        scheduled_date: mainScheduledDate,
        rollover_completion_status: 'pending',
        created_by: currentUserId,
      };

      // Rollover continuation payloads. Lineage is unchanged: 8G.2D.6A's chain
      // law — each part follows the one before it, not the root — and the
      // R-BAY-7 markers that keep continuations out of `uniq_active_bay`.
      // The lineage itself is shaped by buildRolloverChain, the same function
      // OptiPlace uses; the bundle commits every continuation or none.
      const rolloverPayloads: Array<Record<string, unknown>> = [];
      const createdRolloverIds: string[] = [];
      const rolloverOcis: Array<{ id: string; part: number; minutes: number; date: string }> = [];

      if (willCreateRollover && rolloverParts.length > 1) {
        for (let i = 1; i < rolloverParts.length; i++) {
          const link = rolloverChain!.continuations[i - 1];
          const rolloverTicketId = link.id;
          const rolloverStartTime = new Date(link.start_time);
          const rolloverBookMinutes = link.predicted_minutes;
          const rolloverDate = link.scheduled_date;

          rolloverPayloads.push({
            id: rolloverTicketId,
            simple_ticket_number: 0,
            vehicle_id: vehicleData?.id || null,
            customer_name: ticketData.customerName || null,
            customer_phone: ticketData.customerPhone || null,
            type: ticketData.type,
            priority: ticketData.priority,
            is_comeback: ticketData.isComeback,
            assigned_tech_id: topPick.technicianId,
            // D3: a continuation is the same automatic decision as its parent.
            dispatch_assignment_source: 'automatic',
            original_tech_id: topPick.technicianId,
            // B5a: next day's plan claims no bay today, for the same reason
            // as the primary payload above.
            bay_id: null,
            start_time: rolloverStartTime.toISOString(),
            predicted_minutes: rolloverBookMinutes,
            due_out: ticketData.dueOut || null,
            status: 'Active',
            shop_id: activeShop.id,
            parent_ticket_id: link.parent_ticket_id,
            rollover_of_ticket_id: link.rollover_of_ticket_id,
            rollover_type: 'created',
            parking_lot: true,
            parking_spot_id: null,
            sequence_index: link.sequence_index,
            split_reason: link.split_reason,
            scheduled_date: rolloverDate,
            rollover_completion_status: 'pending',
            created_by: currentUserId,
          });

          createdRolloverIds.push(rolloverTicketId);
          rolloverOcis.push({
            id: rolloverTicketId,
            part: i + 1,
            minutes: rolloverBookMinutes,
            date: rolloverDate,
          });
        }
      }

      // ── THE single atomic write ──────────────────────────────────────
      // One transaction, one snapshot, and `__validate_ticket_placement` runs
      // inside it against every group that names a technician — so a
      // placement invalidated between ranking and commit (time off, an
      // unscheduled day, an inactive roster row, an overlapping interval, a
      // lost bay) is refused here EXACTLY as it is for manual OptiPlace,
      // with the same PT409 reason vocabulary.
      await createTicketBundle([
        {
          ticket: primaryTicketPayload,
          jobs: catalystJobsToInsert,
          ticketJobs: catalystTicketJobsToInsert,
        },
        // D1: continuations carry no jobs and no links. Their work lives on
        // the parent.
        ...rolloverPayloads.map(t => ({ ticket: t, jobs: [], ticketJobs: [] })),
      ]);

      // Past this line the primary, every continuation, the jobs and their
      // links are all committed. Nothing below may assume it can undo them,
      // and nothing above may have announced them.

      // OCIS continuation events — optional, post-commit, non-fatal (D2).
      for (const roll of rolloverOcis) {
        logTicketEventToOcis({
          ticketId: roll.id,
          shopId: activeShop.id,
          eventType: 'ticket_created',
          content: `Ticket created via auto-assign EOD rollover (part ${roll.part})`,
          authorUserId: currentUserId || undefined,
          payload: {
            ticketType: ticketData.type,
            totalMinutes: roll.minutes,
            via: 'automatic_assign_rollover',
            rollover_of_ticket_id: ticketId,
            parent_ticket_id: ticketId,
            sequence_index: roll.part,
            scheduled_date: roll.date,
          },
        }).catch(err => console.error('[CatalystAutoAssign] Rollover OCIS log error:', err));
      }

      breadcrumbs.log('OB:COMMIT', { action: 'automatic_assign_complete', ticketId, techId: topPick.technicianId });

      // Trigger board cascade to recalculate schedule after placement
      const { triggerBoardCascade } = await import('@/utils/cascadeTrigger');
      // DCD-A3: cascade routing uses canonical shop-local day label.
      const primaryDateStr = selectedDayLabel;
      await triggerBoardCascade(activeShop.id, primaryDateStr, topPick.technicianId);

      // R38 FIX-4: Cascade ALL rollover dates (not just primary)
      if (createdRolloverIds.length > 0) {
        const rolloverDates = new Set<string>();
        for (const rollId of createdRolloverIds) {
          const { data: rollTicket } = await supabase
            .from('tickets')
            .select('scheduled_date')
            .eq('id', rollId)
            .maybeSingle();
          if (rollTicket?.scheduled_date && rollTicket.scheduled_date !== primaryDateStr) {
            rolloverDates.add(rollTicket.scheduled_date);
          }
        }
        for (const rollDate of rolloverDates) {
          await triggerBoardCascade(activeShop.id, rollDate, topPick.technicianId).catch(err =>
            console.warn('[CatalystAutoAssign] Rollover date cascade error:', err));
        }
      }

      // Log events
      const userId = currentUserId || 'system';
      await logTicketCreated({
        ticketId,
        shopId: activeShop.id,
        userId,
        ticketType: ticketData.type as 'Waiter' | 'Drop-off',
        totalMinutes: mainPredictedMinutes,
        promisedTime: ticketData.dueOut || undefined,
        scheduledStartTime: startTimeToUse,
      });

      await logTicketAssigned({
        ticketId,
        shopId: activeShop.id,
        userId,
        techId: topPick.technicianId,
        techName: topPick.technicianName,
        startTime: startTimeToUse,
      });

      // Cross-device Dayboard sync
      void notifyDayboardInvalidated({
        reason: 'ticket-create',
        shopId: activeShop.id,
        ticketId,
        scheduledDate: primaryDateStr,
        metadata: { techId: topPick.technicianId, autoAssigned: true },
      });

      // Ranking audit (the Ranking tab).
      //
      // CORE-AB-GAP-009 / D3 — the decision-time ranking basis survives the automatic choice. Core-100 R5 (CORE-AB S):
      // the rows are written by the SERVER. smart-assign recorded the ranking it decided on (the exact rows, carrying
      // `explainability_details` with the efficiency and proficiency basis and the closed `missing[]` vocabulary) as
      // a ranking run; this path attaches that run to the ticket it just created and names the technician chosen.
      // The database copies the server's rows verbatim — nothing here writes a score, and nothing re-resolves the
      // basis from the mutable technician row. A ranking the server did not record is reported (pushClientError
      // inside recordAssignmentSelection), never silently accepted.
      void recordAssignmentSelection({
        rankingRecord: result.rankingRecord ?? null,
        ticketId,
        selectedTechId: topPick.technicianId,
        shopId: activeShop.id,
        source: 'catalyst-auto-assign',
      });

      // Show success toast with reasoning
      const rolloverMsg = planWouldRoll ? ' (will not finish today — end-of-day decision needed)' : '';
      toast({
        title: `Assigned to ${topPick.technicianName}`,
        description: `${reason}${rolloverMsg}`,
      });

      setIsAutoAssigning(false);
      options.onSuccess?.(ticketId);
      navigate('/');
      return true;

    } catch (error: any) {
      console.error('[CATALYST-AUTO-ASSIGN] Error:', error);
      breadcrumbs.log('OB:FAIL', { action: 'automatic_assign_error', error: error.message });
      // Core-100 R5 (CORE-AQ): a refused call proposal is not a placement failure — no fallback, nothing was created.
      const callRefusal = callProposalRefusalMessage(error);
      if (callRefusal) {
        toast({ title: 'Call suggestion refused', description: callRefusal, variant: 'destructive' });
        setIsAutoAssigning(false);
        options.onCallProposalRefused?.(callRefusal);
        return false;
      }
      
      toast({
        title: 'Auto-Assignment Failed',
        description: error.message || 'Falling back to manual selection',
        variant: 'destructive',
      });
      
      setIsAutoAssigning(false);
      options.onFallbackToModal?.();
      return false;
    }
  };

  return {
    isCatalystShop,
    isAutoAssigning,
    performAutoAssign,
  };
}
